import { useEffect, useState } from "react";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import type { VendorEndpoint } from "@/types/modelRegistry";
import { formatTokens } from "./format";
import type { RoutingForm } from "./model-form";
import { Field, SelectInput, Skeleton } from "./ui";

interface RoutingFieldsProps {
  modelName: string;
  routing: RoutingForm;
  onChange: (routing: RoutingForm) => void;
}

const usd = new Intl.NumberFormat("en", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 3,
});

function useEndpoints(modelName: string) {
  const [state, setState] = useState<{
    modelName: string;
    endpoints: VendorEndpoint[] | null;
    failure: string | null;
  }>({ modelName: "", endpoints: null, failure: null });
  useEffect(() => {
    if (!modelName) return;
    let live = true;
    modelRegistryApi
      .vendorEndpoints(modelName)
      .then(
        (endpoints) =>
          live &&
          setState({ modelName, endpoints: endpoints ?? [], failure: null }),
      )
      .catch(
        (error) =>
          live &&
          setState({
            modelName,
            endpoints: [],
            failure: registryErrorMessage(error),
          }),
      );
    return () => {
      live = false;
    };
  }, [modelName]);
  return state.modelName === modelName
    ? state
    : { modelName, endpoints: null, failure: null };
}

function EndpointRow({
  endpoint,
  rank,
  onToggle,
}: {
  endpoint: VendorEndpoint;
  rank: number;
  onToggle: () => void;
}) {
  const picked = rank > 0;
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors hover:bg-gray-50 ${picked ? "bg-gray-50/80" : ""}`}
    >
      <input
        type="checkbox"
        className="size-4 rounded border-gray-300 accent-gray-900"
        checked={picked}
        onChange={onToggle}
      />
      <span className="w-5 text-xs tabular-nums text-gray-400">
        {picked ? rank : ""}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-2 text-sm text-gray-900">
          {endpoint.provider}
          {endpoint.quantization && endpoint.quantization !== "unknown" && (
            <span className="rounded bg-gray-100 px-1 text-[11px] text-gray-600">
              {endpoint.quantization}
            </span>
          )}
          {!endpoint.healthy && (
            <span className="text-[11px] text-red-600">down</span>
          )}
        </span>
        <span className="text-xs text-gray-500">
          {formatTokens(endpoint.contextWindow)} ctx
          {endpoint.uptime !== null && ` · ${endpoint.uptime.toFixed(1)}% up`}
          {endpoint.latencyMs !== null &&
            ` · ${Math.round(endpoint.latencyMs)} ms`}
        </span>
      </span>
      <span className="shrink-0 text-xs tabular-nums text-gray-600">
        {endpoint.costInput !== null && endpoint.costOutput !== null
          ? `${usd.format(endpoint.costInput)} / ${usd.format(endpoint.costOutput)}`
          : ""}
      </span>
    </label>
  );
}

/** OpenRouter routing: which upstreams, in what order, and the fallback and sort rules. */
export function RoutingFields({
  modelName,
  routing,
  onChange,
}: RoutingFieldsProps) {
  const { endpoints, failure } = useEndpoints(modelName);
  const set = <K extends keyof RoutingForm>(key: K, value: RoutingForm[K]) =>
    onChange({ ...routing, [key]: value });
  const toggle = (provider: string) =>
    set(
      "order",
      routing.order.includes(provider)
        ? routing.order.filter((p) => p !== provider)
        : [...routing.order, provider],
    );

  if (!modelName) return null;
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-gray-50/40 p-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-gray-900">
          Routing on OpenRouter
        </span>
        <span className="text-xs text-gray-500">
          Tick the upstreams to use, in the order you tick them. None ticked
          means OpenRouter picks.
        </span>
      </div>

      {endpoints === null ? (
        <Skeleton className="h-24" />
      ) : (
        <div className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white">
          {endpoints.map((endpoint) => (
            <EndpointRow
              key={`${endpoint.provider}-${endpoint.tag ?? ""}`}
              endpoint={endpoint}
              rank={routing.order.indexOf(endpoint.provider) + 1}
              onToggle={() => toggle(endpoint.provider)}
            />
          ))}
          {endpoints.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-gray-500">
              {failure ?? "OpenRouter lists no upstreams for this model."}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Sort upstreams by">
          <SelectInput
            value={routing.sort}
            onChange={(e) => set("sort", e.target.value as RoutingForm["sort"])}
            options={[
              { value: "", label: "OpenRouter default" },
              { value: "price", label: "Lowest price" },
              { value: "throughput", label: "Highest throughput" },
              { value: "latency", label: "Lowest latency" },
            ]}
          />
        </Field>
        <Field label="Data collection">
          <SelectInput
            value={routing.dataCollection}
            onChange={(e) =>
              set(
                "dataCollection",
                e.target.value as RoutingForm["dataCollection"],
              )
            }
            options={[
              { value: "", label: "OpenRouter default" },
              {
                value: "deny",
                label: "Only upstreams that do not train on data",
              },
              { value: "allow", label: "Any upstream" },
            ]}
          />
        </Field>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            className="size-4 rounded border-gray-300 accent-gray-900"
            checked={routing.allowFallbacks}
            onChange={(e) => set("allowFallbacks", e.target.checked)}
          />
          Allow fallbacks when the picked upstreams fail
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            className="size-4 rounded border-gray-300 accent-gray-900"
            checked={routing.zdr}
            onChange={(e) => set("zdr", e.target.checked)}
          />
          Zero data retention only
        </label>
      </div>
    </div>
  );
}
